<template>
  <BaseCard class="flex flex-col gap-2 p-4 md:gap-4 md:p-6">
    <div>
      <div class="flex items-center gap-1.5">
        <TextSubtitle>Curtailment</TextSubtitle>
        <WikiTooltipComponent v-if="wikiPath" :path="wikiPath" />
      </div>
      <TextDescription>{{ chartDescription }}</TextDescription>
    </div>
    <LoadingWrapper :loading :error="loadingFailed">
      <template v-if="hasData">
        <div class="h-80">
          <TimeseriesChart
            :chart-type="isShortRange ? 'step' : 'bar'"
            :series="series"
            :axes="axes"
            :data="chartData"
            :x-limits="chartLimits"
            :time-unit="isShortRange ? null : bucketSize"
            aligned-axes
          >
            <!-- Explains what the theoretical production line represents -->
            <template #theoretical="{ label }">
              {{ label }}
              <WikiTooltipComponent
                path="/nl/platform/insights/theoretical-production"
              />
            </template>
          </TimeseriesChart>
        </div>
      </template>
      <p v-else class="text-gray-500 italic">
        Geen curtailment volumes gevonden voor deze selectie.
      </p>
    </LoadingWrapper>
  </BaseCard>
</template>

<script setup>
import { computed } from "vue"
import { startOfDay, endOfDay, startOfMonth, startOfYear } from "date-fns"
import { BUCKET } from "@/helpers/filter.js"
import useUserStore from "@/stores/userStore.js"
import useResultsData from "@/composables/useResultsData.js"
import {
  BaseCard,
  TextSubtitle,
  TextDescription,
  LoadingWrapper,
  TimeseriesChart,
} from "@repowerednl/ui-component-library"
import WikiTooltipComponent from "@/components/information/WikiTooltipComponent.vue"

const props = defineProps({
  meteringPoint: { type: Object, default: null },
  dateRange: { type: Array, required: true },
})

const CURTAILMENT_RECORD_TYPES = [
  "OPTIMA_CURTAILMENT_VOLUME",
  "SMART_ENERGY_SOLAR_TAKE_OFF_VOLUME",
]

const PRODUCTION_RECORD_TYPES = [
  "ALLOCATION_POINT_FEED_IN_VOLUME",
  "ASSET_FEED_IN_VOLUME",
]

const THEORETICAL_PRODUCTION_RECORD_TYPES = ["APE_VOLUME"]

const userStore = useUserStore()

const { recordsData, loading, loadingFailed, bucketSize } = useResultsData(
  () => props.meteringPoint,
  () => props.dateRange,
)

// A single day comes back as quarters. Drawing ~96 bars per day is visually
// busy, so switch to a stepped line there, matching the results chart.
const isShortRange = computed(() => bucketSize.value === BUCKET.MINUTE)

// Round the axis outwards to the bucket, so the first and last bar aren't
// clipped halfway.
const chartLimits = computed(() => {
  switch (bucketSize.value) {
    case BUCKET.MINUTE:
      return [startOfDay(props.dateRange[0]), endOfDay(props.dateRange[1])]
    case BUCKET.MONTH:
      return [
        startOfMonth(props.dateRange[0]),
        startOfMonth(props.dateRange[1]),
      ]
    case BUCKET.YEAR:
      return [startOfYear(props.dateRange[0]), startOfYear(props.dateRange[1])]
    default:
      return [props.dateRange[0], props.dateRange[1]]
  }
})

function findReportedRecordType(recordTypes) {
  return recordTypes.find(
    (recordType) => recordType in (recordsData.value?.types ?? {}),
  )
}

const curtailmentRecordType = computed(() =>
  findReportedRecordType(CURTAILMENT_RECORD_TYPES),
)

const productionRecordType = computed(() =>
  findReportedRecordType(PRODUCTION_RECORD_TYPES),
)

// The theoretical production line is behind a feature flag until the epic that
// delivers it is finished, and only drawn once the API actually reports it.
const canShowTheoretical = computed(
  () =>
    userStore.hasFlag("can-see-theoretical-production") &&
    Boolean(findReportedRecordType(THEORETICAL_PRODUCTION_RECORD_TYPES)),
)

const axes = {
  energy: {
    title: "Productie",
    unit: "Wh",
    minPrefix: "k",
    precision: 0,
  },
}

const series = computed(() => {
  const result = {}
  if (canShowTheoretical.value) {
    // Theoretical production as a line ('ceiling') over the stacked series, so
    // the reached level is explicitly labeled. Declared first on purpose: the
    // chart draws series in reverse order, so this keeps the line on top of the
    // bars instead of behind them.
    result.theoretical = {
      label: "Theoretische productie",
      axis: "energy",
      rescale: 1000,
      color: "gray",
      type: "line",
    }
  }
  // Actual production and curtailment share the same stack: actual at the
  // bottom and curtailment on top together reach the theoretical production.
  // This way the curtailment segment is literally the gap between actual and
  // theoretical production.
  result.actual = {
    label: canShowTheoretical.value ? "Werkelijke productie" : "Productie",
    axis: "energy",
    rescale: 1000,
    color: "energy",
    stack: "production",
  }
  result.curtailment = {
    label: "Curtailment volume",
    axis: "energy",
    rescale: 1000,
    color: "secondary",
    stack: "production",
  }
  return result
})

function sumRecordTypes(data, recordTypes) {
  return recordTypes.reduce(
    (total, recordType) => total + (data[recordType]?.value ?? 0),
    0,
  )
}

// Records are already bucketed by the requested frequency, so the timestamps
// map straight onto the chart's x-values without further aggregation.
const chartData = computed(() => {
  const records = recordsData.value?.records
  if (!records) {
    return {}
  }
  return Object.fromEntries(
    Object.entries(records)
      .filter(([datetime]) => !Number.isNaN(new Date(datetime).getTime()))
      .map(([datetime, { data }]) => {
        const curtailment = sumRecordTypes(data, CURTAILMENT_RECORD_TYPES)
        const production = data[productionRecordType.value]?.value ?? 0
        const theoretical = sumRecordTypes(
          data,
          THEORETICAL_PRODUCTION_RECORD_TYPES,
        )
        return [datetime, { actual: production, curtailment, theoretical }]
      }),
  )
})

const chartDescription = computed(() =>
  canShowTheoretical.value
    ? "Werkelijke en theoretische productie over de geselecteerde periode. Het verschil is je curtailmentvolume: de energie die niet is geproduceerd door curtailment."
    : "Productie over de geselecteerde periode en je curtailmentvolume: de energie die niet is geproduceerd door curtailment.",
)

const wikiPath = computed(
  () =>
    recordsData.value?.types?.[curtailmentRecordType.value]?.wiki_path?.nl ??
    null,
)

const hasData = computed(
  () =>
    Boolean(curtailmentRecordType.value) &&
    Object.keys(chartData.value).length > 0,
)
</script>
